import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { userApi } from '@/services/api'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import styles from './Settings.module.css'

interface ChildProfile {
  id: number
  name: string
  date_of_birth: string
}

export default function Settings() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)

  const [childProfile, setChildProfile] = useState<ChildProfile | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDob, setEditDob] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    // Load child profile
    const loadProfile = async () => {
      try {
        const response = await userApi.getChildProfile()
        setChildProfile(response.data)
        setEditName(response.data.name)
        setEditDob(response.data.date_of_birth)
      } catch {
        // No child profile yet
      }
    }
    loadProfile()
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/welcome')
  }

  const handleEditSave = async () => {
    if (!editName.trim()) {
      setError('Please enter child\'s name')
      return
    }
    if (!editDob) {
      setError('Please enter date of birth')
      return
    }

    setSaving(true)
    setError('')
    try {
      const response = await userApi.updateChildProfile({
        name: editName.trim(),
        date_of_birth: editDob,
      })
      setChildProfile(response.data)
      setIsEditing(false)
    } catch {
      setError('Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action is permanent and cannot be undone.')) {
      return
    }
    if (!window.confirm('This will delete all your data including your child\'s profile, activity history, and subscription. Continue?')) {
      return
    }
    try {
      await userApi.deleteAccount()
      logout()
      navigate('/welcome')
    } catch {
      alert('Failed to delete account. Please try again or contact support.')
    }
  }

  const handleWhatsAppSupport = () => {
    const phone = '919876543210' // Support phone number
    const message = encodeURIComponent(
      `Hi, I need help with MelloMap.\n\nUser ID: ${user?.id || 'unknown'}\nPhone: ${user?.phone_number || 'unknown'}`
    )
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank')
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className={styles.title}>Settings</h1>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Account</h2>
        <div className={styles.card}>
          <div className={styles.row}>
            <span className={styles.label}>Phone</span>
            <span className={styles.value}>{user?.phone_number}</span>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Child Profile</h2>
          {childProfile && !isEditing && (
            <button className={styles.editButton} onClick={() => setIsEditing(true)}>
              Edit
            </button>
          )}
        </div>
        <div className={styles.card}>
          {isEditing ? (
            <div className={styles.editForm}>
              <Input
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
              <Input
                label="Date of Birth"
                type="date"
                value={editDob}
                onChange={(e) => setEditDob(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              {error && <p className={styles.error}>{error}</p>}
              <div className={styles.editButtons}>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEditSave} loading={saving}>
                  Save
                </Button>
              </div>
            </div>
          ) : childProfile ? (
            <>
              <div className={styles.row}>
                <span className={styles.label}>Name</span>
                <span className={styles.value}>{childProfile.name}</span>
              </div>
              <div className={styles.row}>
                <span className={styles.label}>Date of Birth</span>
                <span className={styles.value}>{childProfile.date_of_birth}</span>
              </div>
            </>
          ) : (
            <p className={styles.noProfile}>No child profile yet</p>
          )}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Subscription</h2>
        <div className={styles.card}>
          <button className={styles.link} onClick={() => navigate('/subscription')}>
            <div className={styles.subscriptionRow}>
              <span className={styles.label}>Status</span>
              <span className={styles.subscriptionStatus}>
                {user?.subscription?.status === 'trial' && `Trial (${user.subscription.trial_days_remaining} days left)`}
                {user?.subscription?.status === 'active' && 'Premium'}
                {user?.subscription?.status === 'cancelled' && 'Cancelled'}
                {(!user?.subscription || user?.subscription?.status === 'none') && 'Free'}
              </span>
            </div>
            <span className={styles.arrow}>→</span>
          </button>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Support</h2>
        <div className={styles.card}>
          <button className={styles.link} onClick={handleWhatsAppSupport}>
            Chat with us on WhatsApp
          </button>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Legal</h2>
        <div className={styles.card}>
          <a href="/terms" target="_blank" rel="noopener noreferrer" className={styles.link}>
            Terms of Service
          </a>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className={styles.link}>
            Privacy Policy
          </a>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Account Actions</h2>
        <div className={styles.card}>
          <button className={styles.dangerLink} onClick={handleDeleteAccount}>
            Delete Account
          </button>
          <p className={styles.hint}>This will permanently delete your account and all data</p>
        </div>
      </section>

      <div className={styles.footer}>
        <Button variant="outline" fullWidth onClick={handleLogout}>
          Log out
        </Button>
      </div>
    </div>
  )
}
